import {
  bindable,
  ComponentAttached,
  customElement,
  inject,
} from "aurelia-framework";
import { BaseAureliaEditor } from "ag-grid-aurelia";
import { ICellEditorParams } from "ag-grid";
import { LocaleService } from "../services/locale-service";
import { duration } from "moment";

@customElement("k-grid-editor")
@inject(LocaleService)
export class KGridEditor
  extends BaseAureliaEditor
  implements ComponentAttached
{
  params: ICellEditorParams;
  element: HTMLInputElement;
  localeService: LocaleService;

  @bindable() editorType: KGridEditorType = KGridEditorType.string;
  @bindable() returnAsString: boolean = true;
  @bindable() hasFocus: boolean = false;
  @bindable() invalid: boolean = false;

  /**
   * set whether this editor is editable. Ideally, the value of 'editable'
   * would be derived from the column definition, ie. from
   * "this.params.column.isCellEditable(this.params.node)". But the component
   * definition for ag-grid-column has a bug, which always sets editable to
   * true whenever there's a custom editor, regardless of the column
   * definition (see agGridColumn.ts line 59). Therefore we cannot rely on
   * the column's editable property and have to manage our own editable
   * property.
   */
  @bindable() editable: boolean = true;

  oldValue: any;

  constructor(localeService: LocaleService) {
    super();
    this.localeService = localeService;
  }

  attached(): void {
    this.hasFocus = true;
    this.oldValue = this.params.value;
    // if the context has a function named getEditorType() then call it
    // (with the current params object as argument) to determine the editorType
    if (
      this.params.context &&
      typeof this.params.context.getEditorType === "function"
    ) {
      this.editorType = this.params.context.getEditorType(this.params);
    }
    if (this.params.charPress && this.isValidInput(this.params.charPress)) {
      this.params.value = this.params.charPress;
    }
  }

  getValue(): any {
    return this.invalid
      ? this.oldValue
      : this.returnAsString
        ? this.params.value
        : this.parseInput(this.params.value);
  }

  isCancelBeforeStart(): boolean {
    return !this.editable;
  }

  isCancelAfterEnd(): boolean {
    return this.invalid;
  }

  onInput(event: Event) {
    let value = this.element.value;
    this.invalid = !this.isValidInput(value);
    return true;
  }

  onKeyDown(event: KeyboardEvent) {
    const key = event.key; // note: the 'key' property is always there; it might be polyfilled by library 'keyboardevent-key-polyfill'
    if (key == "ArrowLeft" || key == "ArrowRight") event.stopPropagation();
    else if (
      key != "Backspace" &&
      key != "Delete" &&
      key != "Home" &&
      key != "End" &&
      !this.isKeyAllowed(key)
    ) {
      if (event.preventDefault) event.preventDefault();
    }
    return true;
  }

  private isValidInput(s: string): boolean {
    if (s == "") return true;
    switch (this.editorType) {
      case KGridEditorType.float:
        return this.localeService.isFloat(s);
      case KGridEditorType.int:
        return this.localeService.isInt(s);
      case KGridEditorType.intlist:
        return s.match(/\d+(\s*,\s*\d+)*/) != null;
      case KGridEditorType.boolean:
        return s == "true" || s == "false";
      case KGridEditorType.duration:
        return duration(s).asMilliseconds() > 0;
      case KGridEditorType.string:
      case KGridEditorType.password:
      default:
        return true;
    }
  }

  private parseInput(s: string): any {
    switch (this.editorType) {
      case KGridEditorType.float:
        return this.localeService.parseFloat(s);
      case KGridEditorType.int:
        return this.localeService.parseInt(s);
      case KGridEditorType.boolean:
        return s == "true";
      case KGridEditorType.duration:
        return duration(s);
      case KGridEditorType.string:
      case KGridEditorType.password:
      case KGridEditorType.intlist:
      default:
        return s;
    }
  }

  private isKeyAllowed(key: string): boolean {
    switch (this.editorType) {
      case KGridEditorType.float:
      case KGridEditorType.int:
        return this.isKeyPressedNumeric(key);
      case KGridEditorType.intlist:
        return "0123456789, ".indexOf(key) >= 0;
      case KGridEditorType.boolean:
      case KGridEditorType.duration:
      case KGridEditorType.string:
      case KGridEditorType.password:
      default:
        return true;
    }
  }

  private isKeyPressedNumeric(key: string): boolean {
    return (
      "0123456789".indexOf(key) >= 0 ||
      key == this.localeService.getNegativeSign() ||
      key == this.localeService.getGroupingSeparator() ||
      (this.editorType == KGridEditorType.float &&
        key == this.localeService.getDecimalMark())
    );
  }
}

export enum KGridEditorType {
  string,
  password,
  float,
  int,
  intlist,
  boolean,
  duration,
}
